import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Search, LoaderCircle, Share, Plus, Heart } from 'lucide-react';
import PullToRefresh from 'react-simple-pull-to-refresh';

import { config, callApi } from './NetUtils';
import CompanyCard from './CompanyCard';
import { getJSON, setJSON } from './localStorageUtils';

const HomePage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [offlineDialog, setOfflineDialog] = useState(false);
  const [showFab, setShowFab] = useState(false);
  
  const reportButtonRef = useRef(null);
  const navigate = useNavigate();

  // --- Start: URL-based tab management ---
  const [searchParams, setSearchParams] = useSearchParams();

  // The active tab is now derived directly from the URL search parameter.
  // This makes the URL the single source of truth and fixes back-navigation issues.
  const activeTab = searchParams.get('view') === 'bottom10' ? 'bottom10' : 'top10';

  /**
   * Handles clicks on the tab buttons. It only needs to update the URL;
   * the component will re-render with the correct derived `activeTab`.
   * @param {string} tab - The identifier for the clicked tab ('top10' or 'bottom10').
   */
  const handleTabClick = (tab) => {
    if (tab === 'bottom10') {
      // For 'Bottom 10', set the URL to /?view=bottom10
      setSearchParams({ view: 'bottom10' });
    } else {
      // For 'Top 10', remove the 'view' parameter, resulting in a clean base URL (/).
      setSearchParams({});
    }
  };
  // --- End: URL-based tab management ---


  // Load companies, either from network or from cache
  const fetchCompanies = async () => {
    setLoading(true);

    if (navigator.onLine) {
      try {
        // Fetch both top and bottom companies optimally
        const [topResponse, bottomResponse] = await Promise.all([
          callApi(config.DATA_API_URL, '/reports/top?limit=10'),
          callApi(config.DATA_API_URL, '/reports/bottom?limit=10')
        ]);

        if (topResponse.success && bottomResponse.success) {
          const topCompanies = topResponse.data;
          const bottomCompanies = bottomResponse.data;
          const allCompanies = Array.from(new Map([...topCompanies, ...bottomCompanies].map(c => [c.companyName, c])).values());
          
          setCompanies(allCompanies);
          // cache top/bottom sets
          setJSON('cachedTopCompanies', topCompanies);
          setJSON('cachedBottomCompanies', bottomCompanies);

          // 🚀 Start background prefetching with a web worker
          if (window.Worker) {
            const offlineTopBottomCompaniesReports = new Worker('/worker.js');
            offlineTopBottomCompaniesReports.postMessage({
              companies: allCompanies,
              apiBaseUrl: config.DATA_API_URL
            });

            offlineTopBottomCompaniesReports.addEventListener('message', (event) => {
              const data = event.data;
              // use setJSON from localStorageUtils (available in scope)
              try { setJSON(`reports_${data.companyDetails.companyName}`, data); } catch (e) {
                // as a fallback, attempt direct set
                try { localStorage.setItem(`reports_${data.companyDetails.companyName}`, JSON.stringify(data)); } catch (err) {}
              }
            });
          }
        } else {
          setError(topResponse.error || bottomResponse.error);
        }
      } catch (err) {
        console.error("Error fetching:", err);
        setError("Failed to load data from server.");
      }
    } else {
      // 🚀 Offline fallback - try both cached sets
      const cachedTop = getJSON('cachedTopCompanies', []);
      const cachedBottom = getJSON('cachedBottomCompanies', []);
      if (cachedTop.length > 0 && cachedBottom.length > 0) {
        const allCompanies = Array.from(new Map([...cachedTop, ...cachedBottom].map(c => [c.companyName, c])).values());
        setCompanies(allCompanies);
      } else {
        setOfflineDialog(true);
      }
    }

    setLoading(false);
  };


  useEffect(() => {
    fetchCompanies();
  }, []);

  // Intersection Observer to detect when the "Report anonymously" button is visible
  useEffect(() => {
    const currentRef = reportButtonRef.current;
    
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show FAB when the button is NOT intersecting (out of view)
        setShowFab(!entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: '-50px'
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [loading]); // Re-run when loading changes to ensure button is observed

  // Filter and sort companies based on the active tab and search term.
  const companiesToShow = searchTerm.trim() === ''
    ? activeTab === 'top10' 
      ? JSON.parse(localStorage.getItem('cachedTopCompanies') || '[]')
      : JSON.parse(localStorage.getItem('cachedBottomCompanies') || '[]')
    : companies
        .filter(c => c.companyName.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a, b) => (activeTab === 'top10' ? b.rating - a.rating : a.rating - b.rating));

  const handleShare = async () => {
    const shareData = {
      title: 'AI Transparency Rankings',
      text: `Check out the latest rankings of companies based on their AI usage reports.`,
      url: window.location.href, // Share the URL with the current view parameter
    };
    try {
      await navigator.share(shareData);
    } catch (error) {
      console.error('Error sharing:', error);
      if (error.name !== 'AbortError') {
        alert('Could not open share dialog.');
      }
    }
  };

  /**
   * Generates the appropriate CSS classes for a tab based on whether it is active.
   * @param {string} tabName - The name of the tab to style.
   * @returns {string} A string of Tailwind CSS classes.
   */
  const getTabClass = (tabName) => {
    const baseClasses = 'whitespace-nowrap pb-4 border-b-2 font-medium text-lg';
    const activeClasses = 'border-blue-500 text-blue-600 dark:text-blue-400';
    const inactiveClasses = 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200';
    return `${baseClasses} ${activeTab === tabName ? activeClasses : inactiveClasses}`;
  };

  return (
    <>
      <PullToRefresh onRefresh={fetchCompanies}>
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <LoaderCircle className="w-12 h-12 animate-spin text-blue-600" />
            </div>
          ) : error ? (
            <div className="text-center p-4 bg-red-100 text-red-700 rounded-lg">
              <h3 className="font-bold">Error</h3>
              <p>{error}</p>
            </div>
          ) : (
            <>
              <div className="text-center bg-white dark:bg-gray-800 p-8 md:p-12 rounded-xl shadow-lg border dark:border-gray-700 mb-10">
                <h1 className="text-3xl md:text-5xl font-extrabold mb-4">
                  Shining a Light on AI in the Workplace
                </h1>
                <p className="text-md md:text-lg max-w-2xl mx-auto mb-8">
                  Anonymously report how companies use AI and see how it stacks up on social fairness.
                </p>
                <div ref={reportButtonRef}>
                  <Link
                    to="/report"
                    className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transform hover:scale-105 shadow-xl"
                  >
                    Report anonymously
                  </Link>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-xl shadow-lg border dark:border-gray-700">
                <div className="mb-6 relative">
                  <div className="border-b border-gray-300 dark:border-gray-700 mb-4">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                      <button
                        onClick={() => handleTabClick('top10')}
                        className={getTabClass('top10')}
                        aria-selected={activeTab === 'top10'}
                      >
                        Top 10
                      </button>
                      <button
                        onClick={() => handleTabClick('bottom10')}
                        className={getTabClass('bottom10')}
                        aria-selected={activeTab === 'bottom10'}
                      >
                        Bottom 10
                      </button>
                    </nav>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Scores are based on reports.
                  </p>
                  <button
                    onClick={handleShare}
                    className="absolute top-0 right-0 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Share className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="relative mb-6">
                  <input
                    type="text"
                    placeholder="Search for any company..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-4 pl-12 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600"
                  />
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>

                <div className="space-y-4">
                  {companiesToShow.length > 0 ? (
                    companiesToShow.map((c, i) => (
                      <CompanyCard
                        key={c.companyName}
                        company={c}
                        rank={searchTerm.trim() === '' ? i + 1 : null}
                        sortOrder={activeTab === 'top10' ? 'desc' : 'asc'}
                      />
                    ))
                  ) : (
                    <p className="text-center py-8">No companies found.</p>
                  )}
                </div>
              </div>

              {/* Donation CTA */}
              <div className="mt-10 text-center bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 p-8 md:p-12 rounded-xl shadow-lg border border-pink-200 dark:border-pink-800">
                <Heart className="mx-auto w-12 h-12 text-pink-600 dark:text-pink-400 mb-4" />
                <h2 className="text-2xl md:text-3xl font-bold mb-4">Support Our Mission</h2>
                <p className="text-md md:text-lg max-w-2xl mx-auto mb-6 text-gray-700 dark:text-gray-300">
                  Help us keep this platform free and transparent. Your donation supports our work in promoting AI accountability.
                </p>
                <a
                  href="https://donate.stripe.com/test_dRmaEQ4Sj1oc0no3OO18c00"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-pink-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-pink-700 transform hover:scale-105 shadow-xl"
                >
                  <Heart className="w-6 h-6" />
                  Donate Now
                </a>
              </div>
            </>
          )}
        </div>
      </PullToRefresh>

      {/* Offline fallback modal */}
      {offlineDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg max-w-sm text-center">
            <h2 className="text-xl font-bold mb-4">No Network Connection</h2>
            <p className="mb-4 text-gray-600 dark:text-gray-300">
              You appear to be offline and no cached data is available.
            </p>
            <button
              onClick={() => setOfflineDialog(false)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Floating Action Button (FAB) - appears when main button scrolls out of view */}
      {showFab && (
        <button
          onClick={() => navigate('/report')}
          className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-2xl hover:bg-blue-700 transform hover:scale-110 transition-all duration-300 z-50 animate-fade-in"
          aria-label="Report anonymously"
          style={{ animation: 'fadeIn 0.3s ease-in' }}
        >
          <Plus className="w-8 h-8" />
        </button>
      )}
    </>
  );
};

export default HomePage;
